import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Input, Space, message, Modal, Form, Popconfirm, Tooltip
} from 'antd';
import { PlusOutlined, DeleteOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import { getDepartments, addDepartment, deleteDepartment, updateDepartment } from '../../api/admin';

const DepartmentManage = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const response = await getDepartments();
      if (response.code === 200) {
        setDepartments(response.data);
      }
    } catch (error) {
      message.error('加载部门列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();
      const response = await addDepartment(values);
      if (response.code === 200) {
        message.success('添加成功');
        setAddModalVisible(false);
        form.resetFields();
        loadDepartments();
      } else {
        message.error(response.message || '添加失败');
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.message || '添加失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await deleteDepartment(id);
      if (response.code === 200) {
        message.success('删除成功');
        loadDepartments();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };

  const handleEdit = (record) => {
    setEditingDept(record);
    editForm.setFieldsValue({ name: record.name });
    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      const response = await updateDepartment(editingDept.id, values);
      if (response.code === 200) {
        message.success('修改成功');
        setEditModalVisible(false);
        setEditingDept(null);
        editForm.resetFields();
        loadDepartments();
      } else {
        message.error(response.message || '修改失败');
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.message || '修改失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: '部门名称',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="修改">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定删除该部门?"
            description="删除后无法恢复"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card>
      <h2 style={{ marginBottom: 16 }}>部门管理</h2>

      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddModalVisible(true)}
        >
          添加部门
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={loadDepartments}
        >
          刷新
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={departments}
        rowKey="id"
        loading={loading}
        pagination={false}
      />

      {/* 添加部门弹窗 */}
      <Modal
        title="添加部门"
        open={addModalVisible}
        onOk={handleAdd}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        okText="添加"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="部门名称"
            rules={[
              { required: true, message: '请输入部门名称' },
              { max: 100, message: '部门名称不能超过100个字符' }
            ]}
          >
            <Input placeholder="请输入部门名称" maxLength={100} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改部门弹窗 */}
      <Modal
        title="修改部门"
        open={editModalVisible}
        onOk={handleUpdate}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingDept(null);
          editForm.resetFields();
        }}
        okText="保存"
        cancelText="取消"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="部门名称"
            rules={[
              { required: true, message: '请输入部门名称' },
              { max: 100, message: '部门名称不能超过100个字符' }
            ]}
          >
            <Input placeholder="请输入部门名称" maxLength={100} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default DepartmentManage;
